import { createApp, nextTick, type App } from 'vue'
import AlertModal from '@/components/AlertModal.vue'
import ToastMessage from '@/components/ToastMessage.vue'
import { i18n } from '@/plugins/i18n'

const TOAST_CONTAINER_ID = 'toast-container'

const t = (key: string): string => {
  return i18n.global.t(key) as string
}

function createModal(
  modalType: 'error' | 'warning' | 'success',
  title: string,
  description: string,
  cancelButtonText: string,
  confirmButtonText: string,
  onCancel?: () => void,
  onConfirm?: () => void,
): Promise<boolean> {
  return new Promise((resolve) => {
    const container = document.createElement('div')
    document.body.appendChild(container)

    const app: App = createApp(AlertModal, {
      modalType,
      title,
      description,
      cancelButtonText,
      confirmButtonText,
      onCancel: () => {
        if (onCancel) onCancel()
        resolve(false)
        app.unmount()
        document.body.removeChild(container)
      },
      onConfirm: () => {
        if (onConfirm) onConfirm()
        resolve(true)
        app.unmount()
        document.body.removeChild(container)
      },
    })

    const instance = app.mount(container)
    nextTick(() => {
      const modalInstance = instance as { openModal?: () => void }
      if (modalInstance?.openModal) {
        modalInstance.openModal()
      }
    })
  })
}

export function ErrorPopup(text: string, title?: string | null) {
  if (text === '-')
    return

  return createModal(
    'error',
    title || t('popup.error'),
    text,
    '',
    t('popup.ok'),
  )
}

export function SuccessPopup(text: string) {
  return createModal(
    'success',
    t('popup.success'),
    text,
    '',
    t('popup.ok'),
  )
}

export function WarningPopup(html: string, confimText: string, cancelText?: string) {
  confimText = confimText || t('popup.ok')

  return createModal(
    'warning',
    t('popup.warning'),
    html,
    cancelText || '',
    confimText,
  )
}

export async function ConfirmPopup(
  text: string,
  confimText: string,
  cancelText: string,
  title?: string | null,
) {
  return await createModal(
    'warning',
    title || t('popup.confirm'),
    text,
    cancelText,
    confimText,
  )
}

function getOrCreateToastContainer(): HTMLElement {
  let container = document.getElementById(TOAST_CONTAINER_ID)

  if (!container) {
    container = document.createElement('div')
    container.id = TOAST_CONTAINER_ID
    // Sabit pozisyon, sağ üst köşe, dikey stack
    container.style.cssText = `
      position: fixed;
      top: 16px;
      right: 50%;
      transform: translateX(50%);
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 8px;
      pointer-events: none;
    `
    document.body.appendChild(container)
  }

  return container
}

// Genel toast oluşturma fonksiyonu
function createToast(
  toastType: 'error' | 'warning' | 'success',
  message: string,
  duration: number = 3000,
  icon?: string,
): Promise<void> {
  return new Promise((resolve) => {
    const container = getOrCreateToastContainer()
    const toastWrapper = document.createElement('div')
    toastWrapper.style.pointerEvents = 'auto'
    container.appendChild(toastWrapper)

    const app: App = createApp(ToastMessage, {
      toastType,
      message,
      duration,
      icon,
      onClose: () => {
        resolve()
        app.unmount()
        if (container.contains(toastWrapper)) {
          container.removeChild(toastWrapper)
        }
        // Eğer container boşsa kaldır
        if (container.children.length === 0 && document.body.contains(container)) {
          document.body.removeChild(container)
        }
      },
    })

    app.mount(toastWrapper)
  })
}

export function SuccessToast(text?: string, icon?: string) {
  const toastText = text || t('popup.operation_successful')
  return createToast('success', toastText, 3000, icon)
}

export function ErrorToast(text: string, icon?: string) {
  return createToast('error', text, 3000, icon)
}

export function WarningToast(text: string, icon?: string) {
  return createToast('warning', text, 3000, icon)
}
